import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Flame, Trophy, ShoppingBag, Settings2, Plus, Loader2, ReceiptText, Trash2, RotateCcw, FileText, Download } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WittiesPage = () => {
  const { user, isHR } = useAuth();
  const [balance, setBalance] = useState({ total: 0, redeemed: 0, available: 0 });
  const [leaderboard, setLeaderboard] = useState([]);
  const [merchandise, setMerchandise] = useState([]);
  const [config, setConfig] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [configOpen, setConfigOpen] = useState(false);
  const [editConfig, setEditConfig] = useState(null);
  const [addMerchOpen, setAddMerchOpen] = useState(false);
  const [merchForm, setMerchForm] = useState({ name: '', description: '', cost_witties: '', image_url: '' });
  const [merchImageUploading, setMerchImageUploading] = useState(false);
  const [canManageMerch, setCanManageMerch] = useState(false);
  const [awardOpen, setAwardOpen] = useState(false);
  const [awardForm, setAwardForm] = useState({ employee_id: '', amount: '', reason: '' });
  const [redemptions, setRedemptions] = useState([]);
  const [adminActionLoading, setAdminActionLoading] = useState(null); // 'cleanup' | 'recompute' | null
  const [auditFy, setAuditFy] = useState(() => {
    const now = new Date();
    const y = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
    return `${y}-${String((y + 1) % 100).padStart(2, '0')}`;
  });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [balRes, lbRes, merchRes, confRes, empRes, redRes] = await Promise.allSettled([
        axios.get(`${API_URL}/api/witties/balance`, { withCredentials: true }),
        axios.get(`${API_URL}/api/witties/leaderboard`, { withCredentials: true }),
        axios.get(`${API_URL}/api/witties/merchandise`, { withCredentials: true }),
        axios.get(`${API_URL}/api/witties/config`, { withCredentials: true }),
        axios.get(`${API_URL}/api/employees`, { withCredentials: true }),
        axios.get(`${API_URL}/api/witties/redemptions`, { withCredentials: true }),
      ]);
      if (balRes.status === 'fulfilled') setBalance(balRes.value.data);
      if (lbRes.status === 'fulfilled') setLeaderboard(lbRes.value.data);
      if (merchRes.status === 'fulfilled') setMerchandise(merchRes.value.data);
      if (confRes.status === 'fulfilled') setConfig(confRes.value.data);
      if (empRes.status === 'fulfilled') setEmployees(empRes.value.data);
      if (redRes.status === 'fulfilled') setRedemptions(redRes.value.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    axios.get(`${API_URL}/api/rbac/my-permissions`, { withCredentials: true })
      .then(res => {
        const perms = res.data?.[user?.role] || res.data || {};
        setCanManageMerch(user?.role === 'admin' || !!perms['witties-manage']);
      })
      .catch(() => setCanManageMerch(user?.role === 'admin'));
  }, [user?.role]);

  const handleMerchImageUpload = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Image too large (max 5MB)'); return; }
    setMerchImageUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API_URL}/api/upload?category=merchandise`, fd, { withCredentials: true });
      setMerchForm(f => ({ ...f, image_url: `${API_URL}/api/files/${res.data.path}` }));
      toast.success('Image uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Image upload failed');
    } finally { setMerchImageUploading(false); }
  };

  const handleDeleteMerch = async (itemId) => {
    if (!window.confirm('Remove this merchandise item?')) return;
    try {
      await axios.delete(`${API_URL}/api/witties/merchandise/${itemId}`, { withCredentials: true });
      toast.success('Item removed');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove item');
    }
  };

  const handleCleanupOrphans = async () => {
    if (!window.confirm('Purge Witties balances + logs for all relieved/non-active employees? This cannot be undone.')) return;
    setAdminActionLoading('cleanup');
    try {
      const res = await axios.post(`${API_URL}/api/witties/cleanup-orphans`, {}, { withCredentials: true });
      const names = (res.data.purged_employees || []).map(p => p.name).filter(Boolean);
      toast.success(`${res.data.message}${names.length ? ' — ' + names.join(', ') : ''}`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Cleanup failed');
    } finally { setAdminActionLoading(null); }
  };

  const handleRecomputeWordle = async () => {
    if (!window.confirm('Recompute Wordle Witties for everyone using the corrected logic (+10 only at every 10-day milestone)? Balances will be adjusted with a "Wordle correction" log entry.')) return;
    setAdminActionLoading('recompute');
    try {
      const res = await axios.post(`${API_URL}/api/witties/wordle-recompute`, {}, { withCredentials: true });
      toast.success(`${res.data.message} (Δ adjustments: ${res.data.adjustments_count})`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Recompute failed');
    } finally { setAdminActionLoading(null); }
  };

  const handleAuditCsvExport = async () => {
    try {
      const q = auditFy && auditFy !== 'all' ? `?financial_year=${auditFy}` : '';
      const res = await axios.get(`${API_URL}/api/witties/audit-all/csv${q}`, {
        withCredentials: true, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `witties-audit-all-${auditFy === 'all' ? 'all-time' : 'FY' + auditFy}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('CSV downloaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Audit export failed');
    }
  };

  const handleRedeem = async (itemId) => {
    try {
      const res = await axios.post(`${API_URL}/api/witties/redeem`, { item_id: itemId }, { withCredentials: true });
      toast.success(res.data.message);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Insufficient Witties'); }
  };

  const handleAddMerch = async () => {
    if (!merchForm.name || !merchForm.cost_witties) { toast.error('Name and cost required'); return; }
    try {
      await axios.post(`${API_URL}/api/witties/merchandise`, { ...merchForm, cost_witties: parseInt(merchForm.cost_witties) }, { withCredentials: true });
      toast.success('Merchandise added');
      setAddMerchOpen(false);
      setMerchForm({ name: '', description: '', cost_witties: '', image_url: '' });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleAward = async () => {
    if (!awardForm.employee_id || !awardForm.amount) { toast.error('Select employee and amount'); return; }
    const emp = employees.find(e => e.user_id === awardForm.employee_id || e.id === awardForm.employee_id);
    try {
      await axios.post(`${API_URL}/api/witties/award`, {
        employee_id: awardForm.employee_id, amount: parseInt(awardForm.amount),
        reason: awardForm.reason || 'Admin award', employee_name: emp?.name || '',
      }, { withCredentials: true });
      toast.success('Witties awarded!');
      setAwardOpen(false);
      setAwardForm({ employee_id: '', amount: '', reason: '' });
      fetchData();
    } catch (err) { toast.error('Failed'); }
  };

  const handleSaveConfig = async () => {
    try {
      await axios.put(`${API_URL}/api/witties/config`, editConfig, { withCredentials: true });
      toast.success('Config saved');
      setConfigOpen(false);
      fetchData();
    } catch (err) { toast.error('Failed'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="witties-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center"><Flame className="w-5 h-5 text-white" /></div>
            Witties
          </h1>
          <p className="text-muted-foreground">Earn rewards for streaks, achievements, and on-time work</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {isHR && <Button variant="outline" className="gap-2" onClick={() => setAwardOpen(true)} data-testid="award-witties-btn"><Plus className="w-4 h-4" /> Award</Button>}
          {isHR && (
            <Button variant="outline" className="gap-2" onClick={handleCleanupOrphans} disabled={adminActionLoading === 'cleanup'} data-testid="cleanup-orphans-btn" title="Purge Witties balances + logs for relieved/non-active employees">
              {adminActionLoading === 'cleanup' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
              Clean Up Left Employees
            </Button>
          )}
          {isHR && (
            <Button variant="outline" className="gap-2" onClick={handleRecomputeWordle} disabled={adminActionLoading === 'recompute'} data-testid="recompute-wordle-btn" title="Recompute Wordle Witties using the corrected milestone logic (+10 every 10 days)">
              {adminActionLoading === 'recompute' ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
              Recompute Wordle
            </Button>
          )}
          {isHR && <Button variant="outline" className="gap-2" onClick={() => { setEditConfig(config ? { ...config } : {}); setConfigOpen(true); }}><Settings2 className="w-4 h-4" /></Button>}
        </div>
      </div>

      {/* Balance Card */}
      <Card className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/20 dark:to-orange-950/20 border-amber-200 dark:border-amber-800">
        <CardContent className="p-6">
          <div className="flex items-center gap-6 flex-wrap">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-lg">
              <Flame className="w-9 h-9 text-white" />
            </div>
            <div>
              <p className="text-3xl font-bold text-amber-700 dark:text-amber-400" data-testid="witties-available">{balance.available || 0}</p>
              <p className="text-sm text-muted-foreground">Available Witties</p>
            </div>
            <div className="ml-auto text-right">
              <p className="text-sm"><span className="font-medium" data-testid="witties-total">{balance.total || 0}</span> earned total</p>
              <p className="text-sm"><span className="font-medium">{balance.redeemed || 0}</span> redeemed</p>
            </div>
          </div>
          {/* Source segregation (HR vs Cortex) */}
          <div className="grid grid-cols-2 gap-3 mt-5 pt-4 border-t border-amber-200/60 dark:border-amber-800/40">
            <div className="flex items-center gap-3 p-3 rounded-lg bg-white/60 dark:bg-amber-950/30">
              <div className="w-9 h-9 rounded-md bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center text-blue-700 dark:text-blue-300 font-bold text-xs">HR</div>
              <div>
                <p className="text-lg font-semibold leading-tight" data-testid="witties-earned-hr">{balance.earned_from_hr || 0}</p>
                <p className="text-xs text-muted-foreground">Earned via HR</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 rounded-lg bg-white/60 dark:bg-amber-950/30">
              <div className="w-9 h-9 rounded-md bg-purple-100 dark:bg-purple-900/40 flex items-center justify-center text-purple-700 dark:text-purple-300 font-bold text-[10px]">CORTEX</div>
              <div>
                <p className="text-lg font-semibold leading-tight" data-testid="witties-earned-cortex">{balance.earned_from_cortex || 0}</p>
                <p className="text-xs text-muted-foreground">Earned via Cortex</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="leaderboard">
        <TabsList>
          <TabsTrigger value="leaderboard" className="gap-2"><Trophy className="w-4 h-4" /> Leaderboard</TabsTrigger>
          <TabsTrigger value="merchandise" className="gap-2"><ShoppingBag className="w-4 h-4" /> Merchandise</TabsTrigger>
          {isHR && <TabsTrigger value="redemptions" className="gap-2" data-testid="tab-redemptions"><ReceiptText className="w-4 h-4" /> Redemptions</TabsTrigger>}
          {isHR && <TabsTrigger value="audit" className="gap-2" data-testid="tab-witties-audit"><FileText className="w-4 h-4" /> Audit</TabsTrigger>}
        </TabsList>

        <TabsContent value="leaderboard" className="mt-4">
          <Card>
            <CardHeader><CardTitle className="text-base flex items-center gap-2"><Trophy className="w-4 h-4 text-amber-500" /> Witties Leaderboard</CardTitle></CardHeader>
            <CardContent>
              {leaderboard.length > 0 ? (
                <div className="space-y-2">
                  {leaderboard.map((entry, i) => (
                    <div key={entry.employee_id} className={`flex items-center justify-between p-3 rounded-lg ${i < 3 ? 'bg-amber-50/50 dark:bg-amber-900/10' : 'bg-muted/20'}`}>
                      <div className="flex items-center gap-3">
                        <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${i === 0 ? 'bg-amber-400 text-white' : i === 1 ? 'bg-gray-300 text-gray-700' : i === 2 ? 'bg-amber-700 text-white' : 'bg-muted text-muted-foreground'}`}>{i + 1}</span>
                        <span className="font-medium text-sm">{entry.employee_name || entry.employee_id.slice(0, 8)}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Flame className="w-4 h-4 text-amber-500" />
                        <span className="font-bold text-amber-600">{entry.total}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : <p className="text-center text-muted-foreground py-8">No Witties earned yet. Start your streak!</p>}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="merchandise" className="mt-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold">Redeem Your Witties</h3>
            {canManageMerch && <Button size="sm" variant="outline" className="gap-1" onClick={() => setAddMerchOpen(true)} data-testid="add-merch-btn"><Plus className="w-3 h-3" /> Add Item</Button>}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {merchandise.length > 0 ? merchandise.map(item => (
              <Card key={item.id} data-testid={`merch-${item.id}`}>
                {item.image_url && (
                  <div className="h-36 w-full overflow-hidden rounded-t-lg bg-muted">
                    <img src={item.image_url} alt={item.name} className="h-full w-full object-cover" data-testid={`merch-img-${item.id}`} />
                  </div>
                )}
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <h4 className="font-semibold">{item.name}</h4>
                    {canManageMerch && (
                      <button onClick={() => handleDeleteMerch(item.id)} className="text-muted-foreground hover:text-red-500 transition-colors" title="Remove item" data-testid={`merch-delete-${item.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  {item.description && <p className="text-sm text-muted-foreground mt-1">{item.description}</p>}
                  <div className="flex items-center justify-between mt-3">
                    <div className="flex items-center gap-1"><Flame className="w-4 h-4 text-amber-500" /><span className="font-bold text-amber-600">{item.cost_witties}</span></div>
                    <Button size="sm" onClick={() => handleRedeem(item.id)} disabled={balance.available < item.cost_witties}>Redeem</Button>
                  </div>
                </CardContent>
              </Card>
            )) : <p className="text-muted-foreground col-span-3 text-center py-8">{canManageMerch ? 'No merchandise yet. Add some!' : 'No items available for redemption.'}</p>}
          </div>
        </TabsContent>

        {isHR && (
          <TabsContent value="redemptions" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2"><ReceiptText className="w-4 h-4" /> Merchandise Redemptions ({redemptions.length})</CardTitle>
                <CardDescription>Every Witties-for-merch claim across the organisation, most recent first.</CardDescription>
              </CardHeader>
              <CardContent>
                {redemptions.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No redemptions yet.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="data-table w-full">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Employee</th>
                          <th>Item</th>
                          <th className="text-right">Witties Spent</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {redemptions.map((r) => (
                          <tr key={r.id} data-testid={`redemption-${r.id}`}>
                            <td>{new Date(r.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</td>
                            <td>
                              <div className="font-medium text-sm">{r.employee_name || r.employee_id?.slice(0, 8) || '—'}</div>
                              {r.employee_email && <div className="text-xs text-muted-foreground">{r.employee_email}</div>}
                            </td>
                            <td className="text-sm">{(r.reason || '').replace(/^Redeemed:\s*/i, '') || '—'}</td>
                            <td className="text-right font-semibold text-amber-600">−{r.cost}</td>
                            <td>
                              {r.employee_status === 'active' ? (
                                <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-300">Active</Badge>
                              ) : (
                                <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/30 dark:text-rose-300">{(r.employee_status || 'unknown').toUpperCase()}</Badge>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {isHR && (
          <TabsContent value="audit" className="mt-4" data-testid="audit-tab-content">
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <FileText className="w-4 h-4 text-primary" /> Witties Audit — Org-Wide
                </CardTitle>
                <CardDescription>
                  Pull a per-employee statement of all earnings, redemptions, and corrections — useful for the annual rewards review and balance disputes.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-wrap items-end gap-3">
                  <div className="space-y-1.5 flex-1 min-w-[180px] max-w-[260px]">
                    <Label className="text-xs">Financial Year</Label>
                    <Select value={auditFy} onValueChange={setAuditFy}>
                      <SelectTrigger data-testid="audit-fy-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Time</SelectItem>
                        {(() => {
                          const now = new Date();
                          const curStart = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
                          return [curStart, curStart - 1, curStart - 2].map(y => (
                            <SelectItem key={y} value={`${y}-${String((y + 1) % 100).padStart(2, '0')}`}>
                              FY {y}-{String((y + 1) % 100).padStart(2, '0')} (Apr {y} → Mar {y + 1})
                            </SelectItem>
                          ));
                        })()}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button onClick={handleAuditCsvExport} className="gap-2" data-testid="export-audit-csv">
                    <Download className="w-4 h-4" /> Export CSV
                  </Button>
                </div>
                <div className="rounded-md border bg-muted/30 p-4 text-sm space-y-2">
                  <p className="font-medium">What's in the CSV?</p>
                  <ul className="list-disc list-inside text-xs text-muted-foreground space-y-1">
                    <li>One row per employee with earnings split by source (HR vs Cortex)</li>
                    <li>Redemptions and corrections within the selected fiscal year</li>
                    <li>Current lifetime balance (total / available / redeemed)</li>
                    <li>Org-wide totals at the bottom for reconciliation</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}

      </Tabs>

      {/* Award Dialog */}
      <Dialog open={awardOpen} onOpenChange={setAwardOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Award Witties</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Employee</Label>
              <Select value={awardForm.employee_id} onValueChange={v => setAwardForm({ ...awardForm, employee_id: v })}>
                <SelectTrigger><SelectValue placeholder="Select employee" /></SelectTrigger>
                <SelectContent>{employees.filter(e => e.status === 'active').map(e => <SelectItem key={e.user_id || e.id} value={e.user_id || e.id}>{e.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Witties Amount</Label><Input type="number" value={awardForm.amount} onChange={e => setAwardForm({ ...awardForm, amount: e.target.value })} placeholder="e.g., 10" /></div>
            <div className="space-y-2"><Label>Reason</Label><Input value={awardForm.reason} onChange={e => setAwardForm({ ...awardForm, reason: e.target.value })} placeholder="5-day attendance streak" /></div>
            <Button className="w-full" onClick={handleAward}>Award Witties</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Merchandise Dialog */}
      <Dialog open={addMerchOpen} onOpenChange={setAddMerchOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Add Merchandise</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Item Name</Label><Input value={merchForm.name} onChange={e => setMerchForm({ ...merchForm, name: e.target.value })} placeholder="e.g., Company T-Shirt" data-testid="merch-name-input" /></div>
            <div className="space-y-2"><Label>Description</Label><Input value={merchForm.description} onChange={e => setMerchForm({ ...merchForm, description: e.target.value })} placeholder="Premium cotton, all sizes" data-testid="merch-desc-input" /></div>
            <div className="space-y-2"><Label>Cost (Witties)</Label><Input type="number" value={merchForm.cost_witties} onChange={e => setMerchForm({ ...merchForm, cost_witties: e.target.value })} placeholder="50" data-testid="merch-cost-input" /></div>
            <div className="space-y-2">
              <Label>Item Image</Label>
              {merchForm.image_url ? (
                <div className="relative h-32 w-full overflow-hidden rounded-lg border bg-muted">
                  <img src={merchForm.image_url} alt="preview" className="h-full w-full object-cover" data-testid="merch-image-preview" />
                  <button type="button" onClick={() => setMerchForm({ ...merchForm, image_url: '' })} className="absolute top-1 right-1 rounded-full bg-black/60 p-1 text-white"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              ) : (
                <Input type="file" accept="image/*" disabled={merchImageUploading} onChange={e => handleMerchImageUpload(e.target.files?.[0])} data-testid="merch-image-input" />
              )}
              {merchImageUploading && <p className="text-xs text-muted-foreground">Uploading…</p>}
            </div>
            <Button className="w-full" onClick={handleAddMerch} data-testid="submit-merch-btn" disabled={merchImageUploading}>Add Item</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Config Dialog */}
      <Dialog open={configOpen} onOpenChange={setConfigOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Witties Configuration</DialogTitle></DialogHeader>
          {editConfig && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Streak Rewards</Label>
                {(editConfig.streak_rewards || []).map((sr, i) => (
                  <div key={i} className="flex gap-2">
                    <Input type="number" value={sr.days} onChange={e => { const srs = [...editConfig.streak_rewards]; srs[i] = { ...srs[i], days: parseInt(e.target.value) }; setEditConfig({ ...editConfig, streak_rewards: srs }); }} className="w-20" placeholder="Days" />
                    <Input type="number" value={sr.witties} onChange={e => { const srs = [...editConfig.streak_rewards]; srs[i] = { ...srs[i], witties: parseInt(e.target.value) }; setEditConfig({ ...editConfig, streak_rewards: srs }); }} className="w-24" placeholder="Witties" />
                    <Input value={sr.label} onChange={e => { const srs = [...editConfig.streak_rewards]; srs[i] = { ...srs[i], label: e.target.value }; setEditConfig({ ...editConfig, streak_rewards: srs }); }} className="flex-1" placeholder="Label" />
                  </div>
                ))}
              </div>
              <div className="space-y-2"><Label>Wordle Monthly Winner Witties</Label><Input type="number" value={editConfig.wordle_monthly_witties || 50} onChange={e => setEditConfig({ ...editConfig, wordle_monthly_witties: parseInt(e.target.value) })} /></div>
              <Button className="w-full" onClick={handleSaveConfig}>Save</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WittiesPage;
